import React from 'react';
import { Snackbar, Alert, Button, Box } from '@mui/material';
import { useVersionCheck } from '../hooks/useVersionCheck';
import { useTheme, lighten, darken } from '@mui/material';

/**
 * UpdateNotification Component
 *
 * Displays a notification at the top-center of the screen when a new version is available.
 *
 * Behavior:
 * - Polls /version.json every 5 minutes
 * - Checks immediately when tab regains focus
 * - "Later" button: Hides notification for current browser session only (stored in sessionStorage)
 * - "Update Now" button: Reloads the page to get the latest version
 * - Dismissal is reset when a newer version is detected
 * - Not stored in user preferences - reappears on new browser session
 */
export const UpdateNotification: React.FC = () => {
  const theme = useTheme();
  const baseColor = theme.palette.info.light;
  const tintBg = (color: string) =>
    theme.palette.mode === 'dark' ? darken(color, 0.9) : lighten(color, 0.9);
  const tintBorder = (color: string) =>
    theme.palette.mode === 'dark' ? darken(color, 0.7) : lighten(color, 0.7);
  const {
    updateAvailable,
    onDismiss,
    onUpdate,
    latestVersion,
    currentVersion,
  } = useVersionCheck();

  return (
    <Snackbar
      open={updateAvailable}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{
        // Position at top center
        top: 24,
        // Ensure it appears above app bar and other elements
        zIndex: 5000,
      }}
    >
      <Alert
        severity="info"
        onClose={onDismiss}
        sx={{
          width: '100%',
          alignItems: 'center',
          borderRadius: '8px',
          backgroundColor: tintBg(baseColor),
          border: '1px solid',
          borderColor: tintBorder(baseColor),
          boxShadow: 3,
          '& .MuiAlert-message': {
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            width: '100%',
          },
        }}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              onClick={onDismiss}
              sx={{ whiteSpace: 'nowrap' }}
              title="Hide until next browser session"
            >
              Later
            </Button>
            <Button
              size="small"
              onClick={onUpdate}
              variant="contained"
              color="primary"
              sx={{ whiteSpace: 'nowrap' }}
            >
              Update Now
            </Button>
          </Box>
        }
      >
        <Box>
          <Box sx={{ fontWeight: 'bold', mb: 0.5 }}>
            New version available
          </Box>
          <Box sx={{ fontSize: '0.875rem', opacity: 0.9 }}>
            {latestVersion && currentVersion && latestVersion !== currentVersion && (
              <>Update now to get the latest features and improvements.</>
            )}
            {(!latestVersion || !currentVersion || latestVersion === currentVersion) && (
              <>Update now to get the latest features and improvements.</>
            )}
          </Box>
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default UpdateNotification;